import { useEffect, useRef } from "react";

import PropTypes from "prop-types";

import contentTypes from "@/view/constants/contentTypes";
import videoPlayerEvents from "@/view/constants/videoPlayerEvents";
import useVideoEventBus from "@/view/utils/useVideoEventBus";
import useViewportIsNotDesktop from "@/view/utils/useViewportIsNotDesktop";
import PanelHeaderCloseButton from "./PanelHeaderCloseButton";
import PanelHeaderBackButton from "./PanelHeaderBackButton";
import { rootCss, centeredTitleCss } from "./PanelHeader.style";

const propTypes = {
  backButtonEventProperties: PropTypes.object,
  contentType: PropTypes.string,
  children: PropTypes.node,
  closeButtonEventProperties: PropTypes.object,
  videoQuizIsActive: PropTypes.bool,
  onClickBackButton: PropTypes.func,
  onClickCloseButton: PropTypes.func,
  setActiveVideoActivityLabel: PropTypes.func,
  shouldFocusCloseButtonRef: PropTypes.object,
  shouldRemoveBorderBottom: PropTypes.bool,
};

const PanelHeader = ({
  backButtonEventProperties,
  children,
  closeButtonEventProperties,
  contentType,
  videoQuizIsActive,
  onClickBackButton,
  onClickCloseButton,
  setActiveVideoActivityLabel,
  shouldFocusCloseButtonRef,
  shouldRemoveBorderBottom,
}) => {
  const closeButtonRef = useRef();
  const { sendVideoPlayerEvent, addVideoPlayerEventListener } =
    useVideoEventBus();
  const { VIEW_PAGE_EVENT } = videoPlayerEvents;

  const panelIsAtBottomOfPage = useViewportIsNotDesktop();

  useEffect(() => {
    // Focus the close button when back button is clicked
    if (shouldFocusCloseButtonRef?.current) {
      requestAnimationFrame(() => {
        closeButtonRef?.current?.focus();
      });
      shouldFocusCloseButtonRef.current = false;
    }
  }, [shouldFocusCloseButtonRef?.current]);

  const isVideo = contentType === contentTypes.INTERACTIVE_VIDEO;
  const shouldShowBackButton = isVideo ? videoQuizIsActive : onClickBackButton;
  const shouldShowCloseButton = isVideo
    ? panelIsAtBottomOfPage
    : onClickCloseButton;

  const onVideoBackButtonClick = () => {
    sendVideoPlayerEvent(VIEW_PAGE_EVENT.NAV_BACK_TO_QUIZ_LANDING_PAGE);
    setActiveVideoActivityLabel(null);
  };

  const handleBackClick = () => {
    shouldFocusCloseButtonRef.current = true;

    isVideo ? onVideoBackButtonClick() : onClickBackButton();
  };

  addVideoPlayerEventListener(
    VIEW_PAGE_EVENT.NAV_BACK_TO_QUIZ_LANDING_PAGE,
    () => {
      setActiveVideoActivityLabel(null);
    },
  );

  return (
    <div css={rootCss(shouldRemoveBorderBottom)}>
      {shouldShowBackButton && (
        <PanelHeaderBackButton
          eventProperties={backButtonEventProperties}
          label="Activities"
          onClick={handleBackClick}
        />
      )}
      <div css={shouldShowBackButton && centeredTitleCss}>{children}</div>
      {shouldShowCloseButton && (
        <PanelHeaderCloseButton
          eventProperties={closeButtonEventProperties}
          onClick={onClickCloseButton}
          ref={closeButtonRef}
        />
      )}
    </div>
  );
};

PanelHeader.propTypes = propTypes;

export default PanelHeader;
